import { formatCurrency } from "../../../services/api";

/** Las fechas llegan en UTC; se muestran en hora de Colombia. */
const TIME_ZONE = "America/Bogota";

/** Máximo de boletas por compra que acepta el backend (`invalid_quantity` por encima). */
export const MAX_TICKETS_PER_PURCHASE = 100;

export const PROMO_FILTERS = [
  { id: "active", label: "Activas" },
  { id: "upcoming", label: "Próximas" },
  { id: "inactive", label: "No activas" },
  { id: "all", label: "Todas" },
];

export const DEFAULT_PROMO_FILTER = "active";

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function capitalize(text) {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
}

/** "2026-09-19T01:00:00Z" → "Viernes, 18 de septiembre" (hora Colombia). */
export function formatPromoDay(value) {
  const d = toDate(value);
  if (!d) return "";
  return capitalize(
    d.toLocaleDateString("es-CO", {
      weekday: "long",
      day: "numeric",
      month: "long",
      timeZone: TIME_ZONE,
    })
  );
}

/** "Sábado, 19 de septiembre de 2026, 8:00 p. m." (hora Colombia). */
export function formatPromoDateTime(value) {
  const d = toDate(value);
  if (!d) return "";
  const day = d.toLocaleDateString("es-CO", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: TIME_ZONE,
  });
  const time = d.toLocaleTimeString("es-CO", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: TIME_ZONE,
  });
  return `${capitalize(day)}, ${time}`;
}

/** Igual que `formatPromoDateTime` pero en minúscula, para usar dentro de una frase. */
export function formatPromoDateTimeInline(value) {
  const text = formatPromoDateTime(value);
  return text ? text.charAt(0).toLowerCase() + text.slice(1) : "";
}

/** "19 sept. 2026" (hora Colombia). */
export function formatPromoShortDate(value) {
  const d = toDate(value);
  if (!d) return "";
  return d.toLocaleDateString("es-CO", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: TIME_ZONE,
  });
}

export function promoMoney(value) {
  return formatCurrency(value);
}

export function toAmount(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

export const isRaffle = (promo) => String(promo?.kind || "RAFFLE").toUpperCase() === "RAFFLE";

/**
 * Franja inferior de la tarjeta:
 * - Rifa: "Sorteo por Lotería del Cauca | Sábado, 19 de septiembre"
 * - Promoción informativa: su subtítulo ("Gana más con tu primer depósito | Sólo nuevos clientes").
 */
export function promoStripText(promo) {
  if (!promo) return "";
  if (!isRaffle(promo)) return promo.subtitle || promo.prize_name || "";
  const sponsor = promo.sponsor_lottery?.name;
  const left = sponsor ? `Sorteo por ${sponsor}` : "Sorteo Gold Loterías";
  const day = formatPromoDay(promo.draw_at);
  return day ? `${left} | ${day}` : left;
}

/** Etiqueta + variante (Badge) del estado visible de la promoción. */
export function promoStatusBadge(promo) {
  const display = String(promo?.display_status || "").toLowerCase();
  const status = String(promo?.status || "").toUpperCase();
  if (display === "active") {
    return { label: isRaffle(promo) ? "En venta" : "Vigente", variant: "success" };
  }
  if (display === "upcoming") return { label: "Próximamente", variant: "pending" };
  if (status === "DRAWN") return { label: "Sorteada", variant: "default" };
  if (status === "CANCELLED") return { label: "Cancelada", variant: "error" };
  if (status === "CLOSED") return { label: "Ventas cerradas", variant: "warning" };
  return { label: "Finalizada", variant: "default" };
}

/** Banner de la promoción con las claves que espera `BlogCover`. */
export function promoCoverSource(promo) {
  return {
    cover_image: promo?.banner_image || null,
    cover_image_mobile: promo?.banner_image_mobile || null,
  };
}

/**
 * Mensaje en español para cada `code` de error de la compra.
 * @returns {{ message: string, action?: "recharge"|"play"|"login"|"reload" }}
 */
export function buyErrorInfo(result, raffle) {
  const extra = result?.extra || {};
  const sponsorName =
    (typeof extra.sponsor_lottery === "string" && extra.sponsor_lottery) ||
    raffle?.sponsor_lottery?.name ||
    "la lotería patrocinadora";

  // Sin respuesta definitiva (red caída / 5xx): no se sabe si se cobró; se reintenta con la misma clave.
  if (result?.code === "network" || Number(result?.status) >= 500) {
    return {
      message:
        "No pudimos confirmar tu compra por un problema de conexión. Reintenta: no se te cobrará dos veces.",
    };
  }

  switch (result?.code) {
    case "insufficient_balance": {
      const required = extra.required != null ? promoMoney(extra.required) : "";
      const available = extra.available != null ? promoMoney(extra.available) : "";
      return {
        message:
          required && available
            ? `No tienes saldo suficiente. Necesitas ${required} y tienes ${available}.`
            : "No tienes saldo suficiente para esta compra.",
        action: "recharge",
      };
    }
    case "user_limit_reached": {
      const left = Number(extra.available_for_user);
      return {
        message:
          Number.isFinite(left) && left > 0
            ? `Superas el máximo de boletas por usuario. Aún puedes comprar ${left}.`
            : "Ya alcanzaste el máximo de boletas por usuario para esta rifa.",
        action: "reload",
      };
    }
    case "sold_out":
      return { message: "Las boletas de esta rifa se agotaron.", action: "reload" };
    case "not_enough_tickets": {
      const left = Number(extra.tickets_left);
      return {
        message: Number.isFinite(left)
          ? `No quedan suficientes boletas. Disponibles: ${left}.`
          : "No quedan suficientes boletas para esa cantidad.",
        action: "reload",
      };
    }
    case "sales_closed":
      return { message: "Las ventas de esta rifa ya cerraron.", action: "reload" };
    case "sales_not_started":
      return { message: "Las ventas de esta rifa aún no han comenzado.", action: "reload" };
    case "sponsor_purchase_required":
      return {
        message: `Para participar primero debes comprar un billete de ${sponsorName} dentro de las fechas de la rifa.`,
        action: "play",
      };
    case "not_purchasable":
      return { message: "Esta promoción es informativa: no se compran boletas." };
    case "invalid_quantity":
      return {
        message: `La cantidad no es válida. Puedes comprar entre 1 y ${MAX_TICKETS_PER_PURCHASE} boletas por compra.`,
      };
    case "feature_disabled":
      return { message: "Las promociones no están disponibles en este momento." };
    case "not_found":
      return { message: "No encontramos esta rifa. Puede que ya no esté disponible." };
    case "idempotency_conflict":
      return {
        message: "Detectamos un intento de compra anterior con otros datos. Vuelve a intentarlo.",
      };
    default:
      if (result?.status === 401) {
        return { message: "Tu sesión expiró. Inicia sesión de nuevo.", action: "login" };
      }
      return { message: result?.message || "No pudimos completar la compra. Inténtalo de nuevo." };
  }
}

/**
 * Estado de una boleta en "Mis rifas".
 * @returns {{ label: string, variant: string, key: "won"|"lost"|"pending"|"refunded" }}
 */
export function ticketOutcome(ticket, raffle) {
  const raffleStatus = String(raffle?.status || "").toUpperCase();
  const ticketStatus = String(ticket?.status || "").toUpperCase();
  if (raffleStatus === "CANCELLED" || ticketStatus === "REFUNDED" || ticketStatus === "CANCELLED") {
    return { key: "refunded", label: "Reembolsada", variant: "warning" };
  }
  if (ticket?.is_winner) return { key: "won", label: "Ganadora", variant: "success" };
  if (raffleStatus === "DRAWN") return { key: "lost", label: "No ganó", variant: "error" };
  return { key: "pending", label: "Pendiente", variant: "pending" };
}

/** Estado agregado de una rifa en "Mis rifas" (ganada si alguna boleta ganó). */
export function groupOutcome(group) {
  const tickets = Array.isArray(group?.tickets) ? group.tickets : [];
  const outcomes = tickets.map((t) => ticketOutcome(t, group?.raffle));
  if (outcomes.some((o) => o.key === "won")) return { key: "won", label: "¡Ganaste!", variant: "success" };
  if (String(group?.raffle?.status || "").toUpperCase() === "CANCELLED") {
    return { key: "refunded", label: "Cancelada y reembolsada", variant: "warning" };
  }
  if (String(group?.raffle?.status || "").toUpperCase() === "DRAWN") {
    return { key: "lost", label: "No ganaste", variant: "error" };
  }
  return { key: "pending", label: "Pendiente de sorteo", variant: "pending" };
}

/** Códigos de comprobante únicos, en el orden en que aparecen. */
export function uniqueReceiptCodes(tickets) {
  const seen = new Set();
  (Array.isArray(tickets) ? tickets : []).forEach((t) => {
    if (t?.receipt_code) seen.add(t.receipt_code);
  });
  return Array.from(seen);
}

/** Solo enlaces http(s) (p. ej. `terms_url` absoluto del backend). */
export function safeExternalUrl(url) {
  const s = String(url || "").trim();
  return /^https?:\/\//i.test(s) ? s : "";
}
